<template>
  <v-container>
    <current-user-can permission="editHpiFacilities">
      <v-card>
        <v-card-title>
          HPI Facilities
          <v-spacer />
          <v-text-field
            placeholder="search..."
            v-model="searchtext"
            single-line
            append-icon="mdi-magnify"
            hide-details
          />
          <v-spacer />
          <v-checkbox
            v-model="onlyShowCustomAddresses"
            label="Only Display Custom Addresses?"
            hide-details
            single-line
          />
        </v-card-title>

        <v-data-table
          :search="searchtext"
          :headers="headers"
          :items="hpiDataCount"
          sort-by="count"
          :sort-desc="true"
          class="elevation-1"
          :items-per-page="20"
          @click:row="selectFacility"
          dense
          :loading="loading"
        >
        </v-data-table>
      </v-card>
      <v-dialog card v-model="showEditForm" max-width="40em">
        <v-card>
          <v-card-title>
            Facility {{ selectedFacility.id || "" }}
          </v-card-title>
          <v-tabs v-model="displayTab">
            <v-tab> Edit... </v-tab>
            <v-tab> Merge... </v-tab>
          </v-tabs>
          <v-tabs-items v-model="displayTab">
            <v-tab-item>
              <v-form @submit.prevent="saveFacility">
                <v-card-text>
                  <v-text-field
                    v-model="selectedFacility.name"
                    label="Name"
                    rows="1"
                    auto-grow
                  />

                  <v-textarea
                    label="Address"
                    v-model="selectedFacility.address"
                    rows="1"
                    auto-grow
                  />
                  <pcnz-dhb-select label="DHB" v-model="selectedFacility.dhb" />
                  <v-text-field
                    label="Post Code"
                    v-model="selectedFacility.postcode"
                  />
                  <v-text-field
                    label="HPI Facility Id"
                    v-model="selectedFacility.hpifacilityid"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-menu offset-y v-if="selectedFacility.count">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on">
                        {{ selectedFacility.count }} People work here
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="workAddress in selectedFacility.workaddresses"
                        :key="`workAddress-${workAddress.id}`"
                        :to="`/people/${workAddress.personid}`"
                        target="_blank"
                      >
                        <v-list-item-title>
                          {{ workAddress.personid }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-spacer></v-spacer>
                  <v-btn type="submit" color="primary" :loading="saving">
                    <v-fade-transition transition="fade">
                      <span>
                        Save
                        <v-icon v-show="saved">mdi-check</v-icon>
                      </span>
                    </v-fade-transition>
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-tab-item>
            <v-tab-item>
              <v-form @submit.prevent="mergeFacility">
                <v-card-text>
                  Replace
                  <v-autocomplete
                    :items="hpiDataExcludingSelected"
                    auto-select-first
                    :item-text="
                      item => `${item.name} (has ${item.count} records)`
                    "
                    item-value="hpifacilityid"
                    v-model="mergeWithHpi"
                  />
                  with
                  <strong>
                    {{ selectedFacility.name }}
                  </strong>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn type="submit" color="primary" :loading="saving">
                    <v-fade-transition transition="fade">
                      <span>
                        Merge
                        <v-icon v-show="saved">mdi-check</v-icon>
                      </span>
                    </v-fade-transition>
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-dialog>
    </current-user-can>
  </v-container>
</template>

<script>
export default {
  name: "AdminConditions",
  data() {
    return {
      displayTab: null,
      selectedFacility: {
        name: "",
        address: 0,
        dhb: 0,
        postcode: 0,
        hpifacilityid: 0
      },
      saved: false,
      saving: false,
      headers: [
        {
          text: "Name",
          align: "start",
          value: "name"
        },
        { text: "Address", value: "address" },
        { text: "DHB", value: "dhb" },
        { text: "Postcode", value: "postcode" },
        { text: "HPI facility Id", value: "hpifacilityid" },
        { text: "People", value: "count" },
        { text: "", value: "actions" }
      ],
      hpiData: [],
      showEditForm: false,
      onlyShowCustomAddresses: true,
      searchtext: "",
      mergeWithHpi: {}
    };
  },
  created() {
    this.getFacilities();
  },
  watch: {
    selectedFacility: {
      deep: "true",
      handler(newVal) {
        this.saved = false;
      }
    },
    onlyShowCustomAddresses: "getFacilities"
  },
  computed: {
    hpiDataCount() {
      return this.hpiData.map(f => {
        return { ...f, count: f.workaddresses.length || 0 };
      });
    },
    hpiDataExcludingSelected() {
      return this.hpiDataCount.filter(
        f => f.hpifacilityid !== this.selectedFacility.hpifacilityid
      );
    }
  },
  methods: {
    async getFacilities() {
      this.loading = true;
      try {
        const { data } = await this.$http.get(`hpifacilities/`, {
          params: {
            filter: {
              include: ["workaddresses"],
              where: this.onlyShowCustomAddresses ? { custom: true } : {}
            }
          }
        });
        this.hpiData = [...data];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    selectFacility(item) {
      this.selectedFacility = { ...item };
      this.showEditForm = true;
    },
    async saveFacility() {
      try {
        this.saved = false;
        this.saving = true;
        const workaddresses = this.selectedFacility.workaddresses;
        if (workaddresses && workaddresses[0]) {
          if (workaddresses[0].hpi !== this.selectedFacility.hpifacilityid) {
            this.updateWorkAddressHpis(
              workaddresses,
              this.selectedFacility.hpifacilityid
            );
          }
        }

        await this.$http.patch("/hpifacilities", this.selectedFacility);

        await this.getFacilities();

        this.saved = true;
      } catch (e) {
        this.saveError = e;
      } finally {
        this.saving = false;
      }
    },
    async updateWorkAddressHpis(workaddresses, newHpi) {
      /* eslint-disable-next-line */
      for (let wa of workaddresses) {
        wa.hpi = newHpi;
        await this.$http.patch("/workaddresses", wa);
      }
    },
    async mergeFacility() {
      try {
        console.log("merging: ", this.selectedFacility, this.mergeWithHpi);
        const hpi = this.mergeWithHpi;
        if (!this.mergeWithHpi) {
          return;
        }
        this.saved = false;
        this.saving = true;
        let targetFacility = this.hpiData?.find(f => f.hpifacilityid === hpi);
        if (!targetFacility) {
          return;
        }
        let workaddresses = targetFacility.workaddresses;

        this.updateWorkAddressHpis(
          workaddresses,
          this.selectedFacility.hpifacilityid
        );

        await this.$http.delete(`hpifacilities/${hpi}`);

        await this.getFacilities();
        this.showEditForm = false;

        this.saved = true;
      } catch (e) {
        this.saveError = e;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
